import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { ClaimTypes } from 'src/app/core/enums/claim-type.enum';
import { NavigationService } from 'src/app/shared/services/navigation/navigation.service';
import { StoreHelperService } from 'src/app/shared/services/store-helper/store-helper.service';
import {
  RegisterFormGeneralType,
  RegisterFormType,
} from 'src/app/shared/types/auth.types';
import { ErrorMessages } from 'src/app/shared/utils/error-message.utils';
import { PasswordUtils } from 'src/app/shared/utils/password.utils';
import { AuthAction } from 'src/app/store/auth/auth.action';
import { RegistrationAction } from 'src/app/store/registration/registration.action';
import { RegistrationState } from 'src/app/store/registration/registration.state';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  form!: FormGroup<RegisterFormType>;
  verified: boolean = false;
  errorMessages = ErrorMessages;

  isLoading$: Observable<boolean> = this.store.select(
    RegistrationState.getIsLoading()
  );

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    private route: ActivatedRoute,
    private navigation: NavigationService,
    private storeHelperService: StoreHelperService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      email: this.formBuilder.control(null, [
        Validators.required,
        Validators.email,
      ]),
      password: this.formBuilder.control(null, [
        Validators.required,
        PasswordUtils.passwordValidator(),
      ]),
      confirmPassword: this.formBuilder.control(null, [
        Validators.required,
        PasswordUtils.passwordCompareValidator('password'),
      ]),
      token: this.formBuilder.control(null, Validators.required),
    });

    PasswordUtils.bindPasswordConfirmation(
      this.form,
      'password',
      'confirmPassword'
    );

    this.verifyToken();
  }

  decodeToken(token: string): any {
    const payload = token.split('.')[1];
    const decodedPayload = atob(payload);
    return JSON.parse(decodedPayload);
  }

  onEnterPressed(): void {
    this.register();
  }

  verifyToken() {
    const token = this.route.snapshot.queryParamMap.get('token');

    if (token) {
      this.storeHelperService
        .getAllDispatchAndSelect(new RegistrationAction.VerifyToken(token))
        .pipe(
          tap(() => {
            this.form.get('token')?.setValue(token);
            const decodedToken = this.decodeToken(token);
            const email = decodedToken[ClaimTypes.Email];

            if (email) {
              this.form.get('email')?.setValue(email);
              this.verified = true;
            }
          })
        )
        .subscribe();
    }
  }

  register(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    var body = this.form.value as unknown as RegisterFormGeneralType;
    const { confirmPassword, ...bodyWithoutConfirmPassword } = body;

    this.storeHelperService
      .getAllDispatchAndSelect(
        new AuthAction.CreateAccount(bodyWithoutConfirmPassword)
      )
      .pipe(
        tap(() => {
          this.navigation.goToLogin();
        })
      )
      .subscribe();
  }

  get criteriaList() {
    const password = this.form.get('password')?.value || '';
    return PasswordUtils.getCriteria(password);
  }
}
